import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import * as $ from '../../../../../helpers/merchants/resources/helper';

import {
  LoginQuery,
  LoginWithFbQuery,
  useAuth,
} from '../../../../../hooks/api/auth';
import { SettingUserInfo } from '../../../../../interfaces/merchants/resources/books';
import {
  CustomerFieldsValueType,
  QuestionnaireValueType,
} from '../../../../../interfaces/merchants/resources/books/inputs';

import { setMeToBookInfo } from '../../../../../redux/actions/merchants/resources/books';
import {
  clearUserInfoError,
  inputUserInfo,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';
import { tick } from '../../../../../redux/actions/merchants/resources/books/workflow';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { ReduxState } from '../../../../../redux/reducers';

import BooksLoginSuggestAlert from './components/BooksLoginSuggestAlert';
import BooksWithoutLoginAlert from './components/BooksWithoutLoginAlert';

import InputLogin from '../../../../../molecules/merchant/resources/book/InputLogin';
import InputUserInfoCustomerFields, {
  createInitialState as customerFieldsInitialState,
  submittable as customerFieldsSubmittable,
} from './components/InputUserInfoCustomerFields';
import InputUserInfoQuestionnaires, {
  questionnairesSubmittable,
} from './components/InputUserInfoQuestionnaires';
import InputUserInfoSurvey, {
  submittable as surveySubmittable,
} from './components/InputUserInfoSurvey';
import { useThrottle } from '../../../../../hooks/useThrottle';
import {
  InvalidCustomerInputError,
  PostReservationUserInfoErrorType,
} from '../../../../../redux/actions/merchants/resources/books/api';
import AboveReservationLimitWarning from '../../../../../molecules/common/AboveReservationLimitWarning';
import css from './style.module.scss';
import { useReservationStats } from '../../../../../hooks/api/reservation-stats';
import { useAppDispatch } from '../../../../../redux';
import { ErrorType } from '../../../../../domain/entities/Auth';
import { useUserMe } from '../../../../../hooks/api/me';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import { RSButton } from 'src/reservation-page/RSButton';

enum FieldNames {
  SurveyAnswer = 'surveyAnswer',
  CustomerFields = 'customerFields',
  Questionnaires = 'questionnaires',
}

interface Props {
  settings: SettingUserInfo;
  onSubmit: () => void;
  onRestart: () => void;
  submitLabelIntlId: string;
  merchantPublicId: string;
  resourcePublicId: string;
}

interface InputState {
  customerFields: CustomerFieldsValueType | undefined;
  surveyAnswer: string | undefined;
  questionnaires: QuestionnaireValueType | undefined;
}

export type InputValueType = string | boolean;

const InputUserInfo: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { login, loginWithFb } = useAuth();
  const [errors, setErrors] = useState<ErrorType[]>();
  const { user, isLoggedIn } = useUserMe();

  useEffect(() => {
    scrollToTop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    settings,
    submitLabelIntlId,
    onSubmit,
    onRestart,
    merchantPublicId,
    resourcePublicId,
  } = props;
  const intl = useIntl();

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));

  const [currentEmail, setCurrentEmail] = useState(user?.email);
  const { reservationStats } = useReservationStats({
    merchantPublicId,
    email: currentEmail,
  });

  // emailのinputのonChangeで値をセットしているので、1秒待ってから初回予約かどうか判定する
  const isFirstTimeReservation = useThrottle(
    !!reservationStats?.isFirstTimeReservation,
    1000,
  );

  const { inputs } = useSelector();
  const [showLoginForm, setShowLoginForm] = useState<boolean>(false);
  const [fields, setFields] = useState<InputState>({
    customerFields:
      inputs.userInfo?.customerFields ??
      customerFieldsInitialState(settings.customerFields),
    surveyAnswer: inputs.userInfo?.surveyAnswer ?? settings.survey?.question,
    questionnaires: inputs.userInfo?.questionnaires,
  });
  const [postable, setPostable] = useState(true);

  const submittable = useMemo(() => {
    return (
      customerFieldsSubmittable(
        settings.customerFields,
        fields.customerFields,
      ) &&
      surveySubmittable(settings.survey, fields.surveyAnswer) &&
      questionnairesSubmittable({
        questionnaires: settings.questionnaires,
        values: fields.questionnaires,
        isFirstTimeReservation,
      })
    );
  }, [fields, settings, isFirstTimeReservation]);

  const handleSwitchView = useCallback(() => {
    tick({
      onForwardCallback: () => {
        setShowLoginForm(true);
      },
      onBackCallback: () => {
        setShowLoginForm(false);
      },
    });
    setShowLoginForm(!showLoginForm);
  }, [showLoginForm]);

  const handleChangeEmail = useCallback((email: string) => {
    setCurrentEmail(email);
  }, []);

  const handleChange = useCallback(
    (name: string, value: InputValueType) => {
      setFields(
        $.changeObjectByNestedString(fields, name, value) as InputState,
      );
      dispatch(clearUserInfoError(name));
    },
    [dispatch, fields],
  );

  const [userInfoError, setUserInfoError] = useState<
    PostReservationUserInfoErrorType | undefined
  >(undefined);
  const [invalidInputErrors, setInvalidInputErrors] = useState<
    InvalidCustomerInputError[]
  >([]);

  const handleSubmit = useCallback(async () => {
    setPostable(false);
    try {
      let userInfo = {
        ...fields,
      };
      if (userInfo.customerFields?.birthday) {
        userInfo = {
          ...userInfo,
          customerFields: {
            ...fields.customerFields,
            birthday: fields.customerFields!.birthday.replace(/\//g, '-'),
          },
        };
      }

      const { errorType, errors } = await inputUserInfo({
        reservationId: inputs.reservationId!,
        userInfo,
        settings,
        merchantPublicId,
        dispatch,
      });
      setPostable(true);
      if (errorType) {
        setUserInfoError(errorType);
        if (errors) {
          setInvalidInputErrors(errors);
        }
      } else {
        onSubmit();
      }
    } catch (e) {
      setPostable(true);
      if (e instanceof RestartReservationError) {
        onRestart();
      }
    }
  }, [
    dispatch,
    fields,
    onSubmit,
    onRestart,
    inputs.reservationId,
    settings,
    merchantPublicId,
  ]);

  const handleLogin = useCallback(
    async (_q: LoginQuery) => {
      setPostable(false);
      login(_q.email, _q.password)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
          setShowLoginForm(false);
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [dispatch, inputs.merchantId, login, resourcePublicId],
  );

  const handleLoginWithFb = useCallback(
    async (query: LoginWithFbQuery) => {
      setPostable(false);
      loginWithFb(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
          setShowLoginForm(false);
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [dispatch, inputs.merchantId, loginWithFb, resourcePublicId],
  );

  return (
    <>
      {showLoginForm ? (
        <>
          <BooksWithoutLoginAlert onClick={handleSwitchView} />
          <InputLogin
            postable={postable}
            onLogin={(query: LoginQuery) => handleLogin(query)}
            onLoginWithFb={(query: LoginWithFbQuery) =>
              handleLoginWithFb(query)
            }
            errors={errors}
          />
        </>
      ) : (
        <>
          {!isLoggedIn && <BooksLoginSuggestAlert onClick={handleSwitchView} />}
          {settings.customerFields && (
            <InputUserInfoCustomerFields
              customerFields={settings.customerFields}
              onChange={handleChange}
              onChangeEmail={handleChangeEmail}
              namePrefix={FieldNames.CustomerFields}
              values={fields.customerFields || {}}
              errors={invalidInputErrors}
            />
          )}
          {settings.survey && (
            <InputUserInfoSurvey
              survey={settings.survey}
              onChange={handleChange}
              name={FieldNames.SurveyAnswer}
              value={fields.surveyAnswer || ''}
            />
          )}
          {settings.questionnaires && (
            <InputUserInfoQuestionnaires
              questionnaires={settings.questionnaires}
              onChange={handleChange}
              namePrefix={FieldNames.Questionnaires}
              values={fields.questionnaires || {}}
              isFirstTimeReservation={isFirstTimeReservation}
            />
          )}
          {userInfoError === 'above_reservation_count_limit' && (
            <div className={css.above_reservation_icon}>
              <AboveReservationLimitWarning
                isLoggedIn={isLoggedIn}
                upcomingReservationCount={
                  reservationStats?.userUpcomingReservationCount ?? 0
                }
              />
            </div>
          )}
          {userInfoError === 'customer_blocking' && (
            <div>
              <p className={css.error_msg}>
                {intl.formatMessage({
                  id: 'inputUserInfo.limitReservation',
                })}
              </p>
            </div>
          )}
          {userInfoError === 'above_reservation_count_limit' ||
          userInfoError === 'customer_blocking' ? (
            <div className="px-[18px] py-5">
              <RSButtonAnchor
                variant="outline"
                isBlock
                href={`/${merchantPublicId}`}
              >
                {intl.formatMessage({ id: 'inputUserInfo.backToTop' })}
              </RSButtonAnchor>
            </div>
          ) : (
            <div className="px-[18px] py-5">
              <RSButton
                variant="basic"
                isBlock
                isLoading={!postable}
                disabled={!submittable || !postable}
                value={intl.formatMessage({ id: submitLabelIntlId })}
                onClick={handleSubmit}
              >
                {intl.formatMessage({ id: submitLabelIntlId })}
              </RSButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default InputUserInfo;
